import {
  BulkCreateShiftmanagementShiftShiftsInput,
  ShiftManagementAreasViewPageDocument,
  ShiftManagementBulkCreateShiftsDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import buildEmployeeIds from 'modules/shift_management/lib/buildEmployeeIds'
import {
  trackAddMutationEvent,
  trackAssignEmployeesUnassignedShiftSubmit,
} from 'modules/shift_management/lib/trackEvents'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import Shift from 'modules/shift_management/store/internal/models/Shift'

export interface Props {
  shifts: BulkCreateShiftmanagementShiftShiftsInput[]
  weekDays: string
  isDefaultShift?: boolean
  unassignedShiftCounter?: number
}

const useAddShiftsFromUnassigned = ({
  shifts,
  weekDays,
  isDefaultShift = false,
  unassignedShiftCounter,
}: Props) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchUI] = useUI()
  const { refreshStats } = useRefreshStats()

  const successMessage = unassignedShiftCounter
    ? i18n.t('shift_management.actions.assign_employees_success')
    : i18n.t('shift_management.actions.add_shifts_success')

  const [addShifts, { loading }] = useMutation(ShiftManagementBulkCreateShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementShift,
  })

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const result = await addShifts({
      variables: { shifts },
      // TODO: Actually update the cache properly
      refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      const shiftsData = handled.success ? handled.data?.shifts || [] : []
      const newShifts = shiftsData.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId ?? '0',
        })
      )

      if (unassignedShiftCounter) {
        const impactedEmployees = uniq(shifts.map((s) => String(s.employeeId))).length
        trackAssignEmployeesUnassignedShiftSubmit(
          unassignedShiftCounter,
          impactedEmployees,
          'contextual_button'
        )
      }
      trackAddMutationEvent({ view, isDefaultShift, weekDays, shifts, from: 'modal' })

      dispatchUI({ type: 'REFRESHING_STATS' })
      dispatchSchedule({ type: 'ADD_SHIFTS', shifts: newShifts })
      dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: newShifts, view })

      const { newShiftsRowsKeys, shiftsReferenceKeys } = buildEmployeeIds(
        view,
        shiftsRows,
        newShifts
      )

      closePortal()

      app.setFlash({ type: 'correct', message: successMessage })

      if (newShiftsRowsKeys.length === 0) {
        // Get shiftsRows where there are affected shifts to update stats
        // This is not necessary when we have added new shiftsRows, as
        // refreshShiftsRows has already fetched the stats
        const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
          return shiftsReferenceKeys.includes(row.key)
        })

        await refreshStats(previousShiftsRows)
      }
    }
  }

  return {
    submitting: loading,
    onSubmit,
  }
}

export { useAddShiftsFromUnassigned }
